import { APP_ROUTES } from "@/utils/constants";

export const DIALOG = {
  AddPost: "add-post",
  FeedManager: "feed-manager",
  NotificationViewManager: "notification-view-manager",
  CreateList: "create-list",
  EditList: "edit-list",
  ViewList: "view-list",
  EditPost: "edit-post",
  CreateCustomFeed: "create-custom-feed",
  EditCustomFeed: "edit-custom-feed",
  BuySubscription: "buy-subscription",
  ImageViewer: "image-viewer",
  AccountSocialCard: "account-social-card",
  FeedSettingsComingSoon: "feed-settings-coming-soon",
  ImageEditor: "image-editor",
  PostOverlayEditor: "post-overlay-editor",
  CommentOverlayEditor: "comment-overlay-editor",
  DraftManager: "draft-manager",
  GifSearch: "gif-search",
  GatedContentOptions: "gated-content-options",
  MessageTip: "message-tip",
  PayToUnlockMessage: "pay-to-unlock-message",
  EditorInsertImage: "editor-insert-image",
  EditorInsertLink: "editor-insert-link",
  TradeTransactionSuccess: "trade-transaction-success",
  WalletCashoutSuccess: "wallet-cashout-success",
  GetTokensEarly: "get-tokens-early",
  Holders: "holders",
  Search: "search",
  Follow: "follow",
  NewMessage: "new-message",
  MessageMembers: "message-members",
  Subscribers: "subscribers",
  CustomMessageSettings: "custom-message-settings",
  PollVoters: "poll-voters",
  PostActivity: "post-activity",
  QuotePost: "quote-post",
  FollowUserLists: "follow-user-lists",
  BountyRequest: "bounty-request",
  PageShare: "page-share",
  CreateCollection: "create-collection",
  CreateReferralCode: "create-referral-code",
  ReportPost: "report-post",
  ReportUser: "report-user",
  ManageTier: "manage-tier",
  UpdateEmail: "update-email",
  UpdateProfile: "update-profile",
  Feedback: "feedback",
  ClaimBounty: "claim-bounty",
  UnlockTokens: "unlock-tokens",
  CashoutAmm: "cashout-amm",
  TradeCoin: "trade-coin",
  WalletTopUp: "wallet-top-up",
  WalletSwap: "wallet-swap",
  WalletCashOut: "wallet-cashout",
  WalletSend: "wallet-send",
  TradeCoinMint: "trade-coin-mint",
} as const;

export type DialogName = (typeof DIALOG)[keyof typeof DIALOG];

export type DialogInputParams = Record<
  string,
  boolean | null | number | string | undefined
>;

export type DialogParams = Record<string, string>;
export type DialogHistoryMode = "entry" | "route";

export type RoutedDialog<Name extends DialogName = DialogName> = {
  dialog: Name;
  href: string;
  params: DialogParams;
};

export type RoutedDialogState = {
  activeDialog: RoutedDialog | null;
  href: string | null;
};

const DIALOG_NAMES = new Set<DialogName>(Object.values(DIALOG));

export const isDialogPathname = (pathname: string) => {
  const segments = pathname.split("/").filter(Boolean);

  return segments[0] === APP_ROUTES.dialog.slice(1) && segments.length === 2;
};

const buildHref = (pathname: string, searchParams?: URLSearchParams) => {
  const search = searchParams?.toString();

  return search ? `${pathname}?${search}` : pathname;
};

const normalizeDialogParams = (params?: DialogInputParams): DialogParams => {
  if (!params) {
    return {};
  }

  return Object.entries(params).reduce<DialogParams>((acc, [key, value]) => {
    if (value === null || value === undefined) {
      return acc;
    }

    acc[key] = String(value);
    return acc;
  }, {});
};

export const getNextDialogRef = (dialogs: { ref: number }[]) => {
  return (
    dialogs.reduce((maxRef, dialog) => Math.max(maxRef, dialog.ref), 0) + 1
  );
};

export const isDialogName = (value: string): value is DialogName => {
  return DIALOG_NAMES.has(value as DialogName);
};

export const buildDialogHref = ({
  dialog,
  params,
}: {
  dialog: DialogName;
  params?: DialogInputParams;
}) => {
  const searchParams = new URLSearchParams();

  Object.entries(normalizeDialogParams(params)).forEach(([key, value]) => {
    searchParams.set(key, value);
  });

  return buildHref(`${APP_ROUTES.dialog}/${dialog}`, searchParams);
};

export const parseRoutedDialogState = ({
  pathname,
  search = "",
}: {
  pathname: string;
  search?: string;
}): RoutedDialogState => {
  if (!isDialogPathname(pathname)) {
    return {
      activeDialog: null,
      href: null,
    };
  }

  const segments = pathname.split("/").filter(Boolean);

  const dialog = segments[1];

  if (!isDialogName(dialog)) {
    return {
      activeDialog: null,
      href: null,
    };
  }

  const searchParams = new URLSearchParams(search);
  const href = buildHref(pathname, searchParams);

  return {
    activeDialog: {
      dialog,
      href,
      params: Object.fromEntries(searchParams.entries()),
    },
    href,
  };
};
